import { z } from 'zod';
import { isoDateTimeSchema } from '../common/coercion.js';
import { messageCorrelationIdSchema, uuidSchema } from '../common/correlation.js';
import { flowIdSchema, messageDirectionSchema, systemSchema, } from '../common/systems.js';
/**
 * Grafo de un flujo: el recorrido de un correlation id dibujado como pasos.
 *
 * QUE RESUELVE: el message log es una tabla plana. Para diagnosticar hace falta
 * ver el recorrido —de donde salio el dato, a donde fue, que se rompio en el
 * medio—, y eso en una tabla ordenada por fecha hay que reconstruirlo a mano.
 *
 * LA DECISION CENTRAL: un nodo es un PASO, no un mensaje. Una corrida real de
 * F12 tiene 45 mensajes pero 5 pasos: `syncro.product.search` corrio 37 veces.
 * Dibujar un nodo por mensaje da una tira ilegible de 45 cajas que no se parece
 * a lo que la gente tiene en la cabeza cuando piensa en el flujo. Los mensajes
 * del paso quedan colgando del nodo, a un clic.
 *
 * LO QUE NO ES: esto grafica lo que OCURRIO, no un flujo editable. La logica
 * vive en el codigo; mover una caja aca no cambia el comportamiento de nada.
 */
/**
 * Estado agregado de un paso.
 *
 * `partial` es el que importa y el que no existe a nivel de mensaje: el paso
 * corrio 37 veces, 35 salieron bien y 2 fallaron. Sin este valor habria que
 * elegir entre pintarlo verde (y esconder las 2 fallas) o rojo (y alarmar por
 * un paso que mayormente funciono).
 */
export const flowNodeStatusSchema = z.enum([
    /** Paso esperado por el catalogo del flujo que todavia no ocurrio. */
    'pending',
    /** Hay mensajes en vuelo: recibidos, encolados o procesandose. */
    'running',
    /** Todos los mensajes del paso se procesaron. */
    'ok',
    /** Mezcla: algunos se procesaron y otros fallaron o cayeron en cuarentena. */
    'partial',
    /** Ninguno llego a procesarse: fallaron o murieron. */
    'failed',
    /** Ninguno se proceso y al menos uno quedo en cuarentena. */
    'quarantined',
]);
/**
 * De donde sale una arista.
 *
 * NO ES COSMETICO: `inferred` significa "estos dos pasos ocurrieron uno despues
 * del otro", que no es lo mismo que "este paso causo el otro". Dibujar las dos
 * cosas igual convierte una coincidencia temporal en una afirmacion causal, y
 * quien diagnostica un incidente termina persiguiendo una relacion que nunca
 * existio. La UI pinta `observed` solida y `inferred` punteada.
 */
export const flowEdgeKindSchema = z.enum([
    /** Respaldada por `message_log.parent_message_id`: el padre genero al hijo. */
    'observed',
    /** Deducida del orden temporal dentro del correlation id. */
    'inferred',
    /** Del catalogo del flujo: deberia existir, todavia no ocurrio. */
    'expected',
]);
/**
 * Tope de ids de mensaje que viajan dentro de un nodo.
 *
 * Un paso puede tener miles de mensajes (un lote de catalogo); mandarlos todos
 * infla la respuesta sin que la UI los use. Para ver el resto se navega al
 * listado filtrado por correlation id y evento, que ya esta paginado.
 */
export const FLOW_NODE_MESSAGE_SAMPLE_LIMIT = 50;
/** Un paso del flujo. */
export const flowGraphNodeSchema = z.object({
    /** Estable entre corridas: ver `flowNodeId`. */
    id: z.string().min(1),
    system: systemSchema,
    direction: messageDirectionSchema,
    /** `null` en mensajes viejos que se registraron sin tipo de evento. */
    eventType: z.string().nullable(),
    /** Nombre corto para la caja: ver `flowNodeLabel`. */
    label: z.string().min(1),
    flow: flowIdSchema.nullable(),
    status: flowNodeStatusSchema,
    messageCount: z.number().int().nonnegative(),
    /** Contadores independientes del resultado; opcionales para APIs anteriores. */
    processingCount: z.number().int().nonnegative().optional(),
    waitingCount: z.number().int().nonnegative().optional(),
    /**
     * Mensajes en `failed` o `dead`. NO incluye los de cuarentena, que se cuentan
     * aparte porque son otro problema: la cuarentena es un contrato que no calza,
     * no un envio que no salio. Sumarlos aqui haria que `messageCount - failedCount`
     * —la cuenta que la UI hace sola— sobreestime los exitos.
     */
    failedCount: z.number().int().nonnegative(),
    /** Mensajes en cuarentena. Ver `failedCount`. */
    quarantinedCount: z.number().int().nonnegative(),
    /**
     * Posicion del paso en la variante del flujo, si esta declarado en el catalogo.
     *
     * ES LO QUE PERMITE COLOCAR UN `pending`: un paso que no ocurrio no tiene
     * fecha, asi que sin este numero la UI no tendria NINGUN dato para ubicarlo y
     * tendria que rehacer el cruce contra su propia copia del catalogo —que puede
     * ser de otra version que la que uso la API para armar el grafo—.
     */
    order: z.number().int().nonnegative().nullable(),
    /** Que hace el paso, del catalogo. `null` si el paso no esta declarado. */
    description: z.string().nullable(),
    /** Si el catalogo lo declara condicional. `null` si el paso no esta declarado. */
    optional: z.boolean().nullable(),
    /** `null` en un nodo `pending`: no ocurrio, no tiene fechas. */
    firstAt: isoDateTimeSchema.nullable(),
    lastAt: isoDateTimeSchema.nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    /**
     * ULTIMO error del paso —el mas reciente—, para verlo sin abrir el detalle.
     *
     * El nombre manda: es el ultimo, no el primero. En un paso que reintenta, el
     * primero suele ser el sintoma inicial y el ultimo el estado en que quedo, que
     * es lo que se mira al abrir la pantalla.
     */
    lastError: z.string().nullable(),
    /**
     * ULTIMA nota del paso —la mas reciente—, con el mismo criterio que
     * `lastError`: la observacion de un resultado esperado que no es 2xx (el
     * -4006 de SAP antes de cargar el tipo de cambio). No es un error y no cambia
     * el estado: un mensaje con nota esta `processed`. Opcional, como
     * `processingCount`, para APIs anteriores.
     */
    note: z.string().nullable().optional(),
    messageIds: z.array(uuidSchema).max(FLOW_NODE_MESSAGE_SAMPLE_LIMIT),
    /** `true` cuando el paso tiene mas mensajes que los que vienen en `messageIds`. */
    messageIdsTruncated: z.boolean(),
});
/** Una conexion entre dos pasos. */
export const flowGraphEdgeSchema = z.object({
    id: z.string().min(1),
    /** `flowGraphNode.id` de origen. */
    source: z.string().min(1),
    /** `flowGraphNode.id` de destino. */
    target: z.string().min(1),
    kind: flowEdgeKindSchema,
    /** Mensajes que respaldan la arista. Siempre 0 en una `expected`. */
    messageCount: z.number().int().nonnegative(),
});
/**
 * Otra corrida causalmente unida a esta.
 *
 * El correlation id encadena UNA corrida. Un pedido que se crea (F1) y despues
 * se factura (F10) son dos corridas distintas, y sin esto "de donde vino esto"
 * se corta en el borde de la corrida.
 *
 * SALE DE LA CAUSALIDAD, NO DE UNA HEURISTICA: un mensaje de esta corrida cuyo
 * padre vive en otro correlation id es prueba de que una corrida gatillo a la
 * otra. Relacionarlas por entidad compartida —el mismo SKU, el mismo pedido—
 * tambien seria posible, pero une corridas que solo coincidieron en el dato sin
 * que ninguna haya causado a la otra. Cuando haga falta se agrega como un
 * `reason` mas, y quien mira sabra cual de las dos cosas esta viendo.
 */
export const flowGraphRelationSchema = z.object({
    correlationId: messageCorrelationIdSchema,
    flow: flowIdSchema.nullable(),
    /** `parent`: aquella corrida gatillo a esta. `child`: esta gatillo a aquella. */
    reason: z.enum(['parent', 'child']),
    /** Mensajes que sostienen la relacion. */
    messageCount: z.number().int().nonnegative(),
    startedAt: isoDateTimeSchema.nullable(),
});
/** El grafo completo de una corrida. */
export const flowGraphSchema = z
    .object({
    correlationId: messageCorrelationIdSchema,
    /** Flujo dominante de la corrida. `null` si ningun mensaje lo declara. */
    flow: flowIdSchema.nullable(),
    /** Estado de la corrida entera, agregado con las mismas reglas que un nodo. */
    status: flowNodeStatusSchema,
    startedAt: isoDateTimeSchema.nullable(),
    endedAt: isoDateTimeSchema.nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    messageCount: z.number().int().nonnegative(),
    nodes: z.array(flowGraphNodeSchema),
    edges: z.array(flowGraphEdgeSchema),
    related: z.array(flowGraphRelationSchema),
})
    .superRefine((graph, ctx) => {
    /**
     * Una arista que apunta a un nodo inexistente, o un lazo sobre el mismo
     * paso, no se ve como un error: se ve como un grafo raro. Falla aca, con el
     * detalle, en vez de llegar a la pantalla como una flecha al vacio.
     */
    const ids = new Set(graph.nodes.map((node) => node.id));
    graph.edges.forEach((edge, index) => {
        if (edge.source === edge.target) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['edges', index],
                message: `La arista ${edge.id} sale y entra en el mismo paso.`,
            });
        }
        for (const extremo of ['source', 'target']) {
            if (!ids.has(edge[extremo])) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['edges', index, extremo],
                    message: `La arista ${edge.id} apunta a ${edge[extremo]}, que no es un nodo del grafo.`,
                });
            }
        }
    });
});
/**
 * Identidad de un paso dentro de una corrida.
 *
 * Se arma con los tres campos que definen "el mismo paso" y con ninguno mas: el
 * id tiene que ser igual en dos corridas distintas del mismo flujo para poder
 * comparar una corrida sana con una rota, y estable entre recargas para que la
 * UI no reposicione las cajas sola.
 */
export function flowNodeId(input) {
    return `${input.direction}:${input.system}:${input.eventType ?? 'sin-evento'}`;
}
/**
 * Nombre corto para la caja.
 *
 * Los `event_type` empiezan por su sistema (`syncro.catalog.load`) y ese prefijo
 * ya esta en el color y en la etiqueta del nodo: repetirlo gasta el ancho de la
 * caja, que es el recurso escaso del grafo.
 */
export function flowNodeLabel(input) {
    if (input.eventType === null || input.eventType.length === 0) {
        return 'sin evento';
    }
    const prefix = `${input.system}.`;
    return input.eventType.startsWith(prefix)
        ? input.eventType.slice(prefix.length)
        : input.eventType;
}
/**
 * Estado de un paso a partir de los estados de sus mensajes.
 *
 * El orden de las reglas ES el contrato, porque un paso mezcla estados y hay que
 * elegir cual gana. Manda lo que exige actuar: un fallo no se esconde detras de
 * treinta exitos. Y si hubo exitos, el resultado es `partial` y no `failed`,
 * para no mandar a revisar un paso que en su mayoria funciono.
 */
export function aggregateFlowNodeStatus(statuses) {
    if (statuses.length === 0) {
        return 'pending';
    }
    const has = (status) => statuses.includes(status);
    const succeeded = has('processed');
    if (has('failed') || has('dead')) {
        return succeeded ? 'partial' : 'failed';
    }
    if (has('quarantined')) {
        return succeeded ? 'partial' : 'quarantined';
    }
    if (has('received') || has('queued') || has('processing')) {
        return 'running';
    }
    return 'ok';
}
//# sourceMappingURL=flow-graph.js.map